import { useState } from "react";
import { useGoalPinPortfolioMutation } from "web_myplann/Api/Goals/useGoalPinPortfolioMutation";
import { ALLOCATION_MIN } from "web_myplann/Goals/Hooks/useAllocationLimits";
import {
	type GoalDetailResponseDTO,
	type GoalPortfolioListItemResponseDTO,
	type GoalPortfolioRequestDTO,
	type GoalPortfolioResponseDTO,
	GoalPortfolioTypeEnum,
	type UpdateGoalRequestDTO,
} from "web_myplann/Infrastructure/Api/Api";

type UseGoalPortfolioActionsProps = {
	goalId: number;
	goalDto: GoalDetailResponseDTO | undefined;
	onPinSettled?: () => void;
};

const toRequestPortfolio = (
	p: GoalPortfolioRequestDTO | GoalPortfolioResponseDTO,
): GoalPortfolioRequestDTO => ({
	portfolioId: p.portfolioId,
	usagePercent: p.usagePercent,
	type: p.type,
});

const buildUpdateBody = (
	dto: GoalDetailResponseDTO,
	portfolios: GoalPortfolioRequestDTO[],
): UpdateGoalRequestDTO => ({
	name: dto.name,
	investmentHorizonEndDate: dto.targetDate,
	currencyCode: dto.currencyCode,
	oneTimePayment: dto.oneTimePayment,
	periodicPayment: dto.periodicPayment,
	portfolios,
});

export const useGoalPortfolioActions = ({
	goalId,
	goalDto,
	onPinSettled,
}: UseGoalPortfolioActionsProps) => {
	const [unpinningId, setUnpinningId] = useState<number | null>(null);
	const { mutate } = useGoalPinPortfolioMutation();

	const pin = (
		picked: GoalPortfolioListItemResponseDTO,
		type: GoalPortfolioTypeEnum,
	) => {
		if (!goalDto) return;

		const existing = goalDto.portfolios ?? [];
		const alreadyPinned = existing.some((p) => p.portfolioId === picked.id);
		const isSetAside = type === GoalPortfolioTypeEnum.SetAside;
		const available = Math.max(0, 100 - picked.usagePercent);
		if (alreadyPinned || (!isSetAside && available < ALLOCATION_MIN)) {
			onPinSettled?.();
			return;
		}

		mutate(
			{
				id: goalId,
				body: buildUpdateBody(goalDto, [
					...existing.map(toRequestPortfolio),
					{
						portfolioId: picked.id,
						usagePercent: isSetAside ? 100 : available,
						type,
					},
				]),
			},
			{ onSettled: () => onPinSettled?.() },
		);
	};

	const unpin = (portfolioId: number) => {
		if (!goalDto) return;

		const remaining = (goalDto.portfolios ?? [])
			.filter((p) => p.portfolioId !== portfolioId)
			.map(toRequestPortfolio);

		setUnpinningId(portfolioId);
		mutate(
			{
				id: goalId,
				body: buildUpdateBody(goalDto, remaining),
			},
			{ onSettled: () => setUnpinningId(null) },
		);
	};

	const editAllocation = (
		portfolioId: number,
		usagePercent: number,
		options?: { onSettled?: () => void },
	) => {
		if (!goalDto) return;

		const updated = (goalDto.portfolios ?? []).map((p) =>
			p.portfolioId === portfolioId
				? { portfolioId: p.portfolioId, usagePercent, type: p.type }
				: toRequestPortfolio(p),
		);

		mutate(
			{
				id: goalId,
				body: buildUpdateBody(goalDto, updated),
			},
			{ onSettled: () => options?.onSettled?.() },
		);
	};

	return { pin, unpin, editAllocation, unpinningId };
};
